import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Listbox } from "@/components/ui/form";
import { SlotTime } from "../slot-time";
import { PlusIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";

export const AvailabilitySlots = ({ day }) => {
    const { control, setValue, clearErrors, getValues } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${day}.Times`,
    });

    const applyTo = useWatch({
        control,
        name: `${day}.applyTo`,
    });

    const times = useWatch({
        control,
        name: `${day}.Times`,
    });

    const validTimes = times.filter((time) => time.startTime && time.endTime);

    const addSlot = () => {
        append({
            availabilityGuid: "",
            startTime: "",
            endTime: "",
        });
    };

    const onRemove = (index) => {
        remove(index);

        if (fields.length === 1) {
            setValue(`${day}.enabled`, false);
            clearErrors(`${day}.Times`);
        }
    };

    const copyToSelectedDays = () => {
        applyTo.forEach((applyToDay) => {
            if (applyToDay === day) return;

            setValue(`${applyToDay}.enabled`, true);

            setValue(
                `${applyToDay}.Times`,
                times.map((time) => ({
                    availabilityGuid: "",
                    startTime: time.startTime,
                    endTime: time.endTime,
                })),
            );
        });
    };

    const daysOfWeek = [
        { value: "Mon", label: "Monday" },
        { value: "Tue", label: "Tuesday" },
        { value: "Wed", label: "Wednesday" },
        { value: "Thu", label: "Thursday" },
        { value: "Fri", label: "Friday" },
        { value: "Sat", label: "Saturday" },
        { value: "Sun", label: "Sunday" },
    ];

    const copyToWeekdays = () => {
        const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri"];

        weekdays.forEach((targetDay) => {
            if (targetDay === day) return;

            const targetTimes = getValues(`${targetDay}.Times`) || [];

            const newTimes = times.map(({ startTime, endTime }) => {
                const existing = targetTimes.find(
                    (targetTime) =>
                        targetTime.startTime === startTime &&
                        targetTime.endTime === endTime,
                );

                return (
                    existing || {
                        availabilityGuid: "",
                        startTime,
                        endTime,
                    }
                );
            });

            setValue(`${targetDay}.enabled`, true);
            setValue(`${targetDay}.Times`, newTimes);
        });
    };

    return (
        <div className="relative -mx-1 overflow-x-auto">
            <table className="w-full max-w-100 table-fixed text-left">
                <thead>
                    <tr>
                        <th scope="col" className="w-[39%] px-1 font-medium">
                            Start time
                        </th>
                        <th scope="col" className="w-[39%] px-1 font-medium">
                            End time
                        </th>
                        <th
                            scope="col"
                            className="w-[22%] px-1 font-medium"
                        ></th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, index) => (
                        <tr key={field.id} className="align-top">
                            <SlotTime
                                day={day}
                                index={index}
                                onRemove={onRemove}
                                availabilityGuid={field.availabilityGuid}
                            />
                        </tr>
                    ))}
                    <tr>
                        <td className="px-1 py-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={addSlot}
                            >
                                <PlusIcon />
                                Add another time slot
                            </Button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="px-1 py-2">
                <p>Apply to: </p>
                <div className="w-50">
                    <Listbox
                        name={`${day}.applyTo`}
                        size="sm"
                        label="Selected days"
                        placeholder="What is your role?"
                        hideLabel={true}
                        multiple
                    >
                        {daysOfWeek.map((dayOfWeek) => (
                            <option
                                value={dayOfWeek.value}
                                disabled={dayOfWeek.value == day}
                            >
                                {dayOfWeek.label}
                            </option>
                        ))}
                    </Listbox>
                </div>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="mt-2 w-50"
                    onClick={copyToSelectedDays}
                    disabled={
                        JSON.stringify(applyTo) === JSON.stringify([day]) ||
                        !applyTo.length ||
                        !validTimes.length
                    }
                >
                    <DocumentDuplicateIcon />
                    Copy to selected days
                </Button>
                {day != "Sat" && day != "Sun" && (
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="mt-2 w-50"
                        onClick={copyToWeekdays}
                        disabled={!validTimes.length}
                    >
                        <DocumentDuplicateIcon />
                        Copy to all weekdays
                    </Button>
                )}
                <div className="mt-5 px-1">
                    <p className="text-sm text-neutral-600">
                        End time = when your last appointment finishes, not when
                        it starts.
                    </p>
                </div>
            </div>
        </div>
    );
};
